import { TIER_HEX, TIER_LABELS, type WorkerTier } from '../../../modules/core/index.js'
import { DivergingBars } from '../charts/DivergingBars.js'
import { StackedBars } from '../charts/StackedBars.js'
import { TIER_ORDER, sumByPeriod } from '../insightsSeries.js'
import { monthLabel } from '../labels.js'
import type { FacilityMonthRollup } from '../types.js'

/**
 * Tiers (plan section 6.3): the tier mix at each close as a 100% stack, the workers moved up above the line and down
 * below each close, and the twelve closes added up. Time in tier is per worker and waits (D8).
 */
export function TiersScreen({ rollups, periods }: { rollups: readonly FacilityMonthRollup[]; periods: readonly string[] }) {
  const closed = rollups.filter((rollup) => rollup.closed)
  const layers = TIER_ORDER.map((tier) => ({ key: tier, name: TIER_LABELS[tier as WorkerTier], color: TIER_HEX[tier as WorkerTier], values: sumByPeriod(closed, periods, (rollup) => rollup.tiers[tier]) }))
  const up = sumByPeriod(closed, periods, (rollup) => rollup.tierChanges.up)
  const down = sumByPeriod(closed, periods, (rollup) => rollup.tierChanges.down)
  const totals = closed.reduce(
    (sum, rollup) => ({ up: sum.up + rollup.tierChanges.up, down: sum.down + rollup.tierChanges.down, held: sum.held + rollup.tierChanges.held, newHire: sum.newHire + rollup.tierChanges.newHire, provisionalHeld: sum.provisionalHeld + rollup.tierChanges.provisionalHeld, noScore: sum.noScore + rollup.tierChanges.noScore }),
    { up: 0, down: 0, held: 0, newHire: 0, provisionalHeld: 0, noScore: 0 },
  )
  const latestPeriod = closed.reduce<string | null>((found, rollup) => (found === null || rollup.period > found ? rollup.period : found), null)
  const latest = latestPeriod === null ? [] : closed.filter((rollup) => rollup.period === latestPeriod)
  const latestTiers = TIER_ORDER.map((tier) => latest.reduce((sum, rollup) => sum + rollup.tiers[tier], 0))
  const latestTotal = latestTiers.reduce((sum, count) => sum + count, 0)

  if (closed.length === 0) {
    return <div className="rounded-lg border border-marine/15 bg-white px-4 py-8 text-center text-xs text-ink-mid">No month in this span has closed yet. Tiers are set at the close.</div>
  }
  return (
    <div className="flex flex-col gap-6">
      <section aria-label="Tier summary" className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        {TIER_ORDER.map((tier, index) => (
          <div key={tier} className="rounded-lg border border-marine/15 bg-white px-4 py-3" style={{ boxShadow: `inset 4px 0 0 ${TIER_HEX[tier as WorkerTier]}` }}>
            <div className="text-[10px] font-bold uppercase tracking-wide text-ink-mid">{TIER_LABELS[tier as WorkerTier]}</div>
            <div className="flex items-baseline gap-2">
              <span className="text-2xl font-black text-ink">{latestTiers[index]}</span>
              <span className="text-[11px] text-ink-mid">{latestTotal > 0 ? `${Math.round(((latestTiers[index] ?? 0) / latestTotal) * 100)}%` : ''}</span>
            </div>
            <div className="text-[10px] text-ink-mid">{latestPeriod ? `at the ${monthLabel(latestPeriod, 'long')} close` : ''}</div>
          </div>
        ))}
      </section>

      <div className="grid gap-6 lg:grid-cols-2">
        <section className="flex min-w-0 flex-col gap-3 rounded-lg border border-marine/15 bg-white px-4 py-3">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="flex-1 text-sm font-black text-ink">Tier mix by month</h2>
            <span className="text-[11px] text-ink-mid">Share of workers at each close</span>
          </div>
          <StackedBars periods={periods} layers={layers} normalized periodLabel={monthLabel} format={(value) => String(Math.round(value))} title="Share of workers by tier at each close" />
          <div className="flex flex-wrap gap-3 text-[11px] text-ink-mid">
            {layers.map((layer) => (
              <span key={layer.key} className="inline-flex items-center gap-1.5">
                <span className="inline-block h-3 w-3 rounded-sm" style={{ background: layer.color }} />
                {layer.name}
              </span>
            ))}
          </div>
        </section>
        <section className="flex min-w-0 flex-col gap-3 rounded-lg border border-marine/15 bg-white px-4 py-3">
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="flex-1 text-sm font-black text-ink">Net movement each close</h2>
            <span className="text-[11px] text-ink-mid">Moved up above the line, moved down below</span>
          </div>
          <DivergingBars periods={periods} up={up} down={down} upName="Moved up" downName="Moved down" periodLabel={monthLabel} title="Workers moved up and down a tier at each close" />
          <div className="flex flex-wrap gap-3 text-[11px] text-ink-mid">
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block h-3 w-3 rounded-sm" style={{ background: '#2E8B57' }} /> Moved up
            </span>
            <span className="inline-flex items-center gap-1.5">
              <span className="inline-block h-3 w-3 rounded-sm" style={{ background: '#D9553F' }} /> Moved down
            </span>
          </div>
        </section>
      </div>

      <section className="rounded-lg border border-marine/15 bg-white px-4 py-3">
        <h2 className="text-sm font-black text-ink">The closes added up</h2>
        <dl className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-xs md:grid-cols-3">
          {[
            ['Moved up', totals.up, '#2E8B57'],
            ['Moved down', totals.down, '#D9553F'],
            ['Held', totals.held, null],
            ['New hire, 90-day rule', totals.newHire, null],
            ['Provisional, held', totals.provisionalHeld, null],
            ['No score that month', totals.noScore, null],
          ].map(([label, value, color]) => (
            <div key={String(label)} className="flex justify-between border-b border-marine/10 py-1">
              <dt className="text-ink-mid">{label}</dt>
              <dd className="font-bold" style={color ? { color: String(color) } : undefined}>
                {value}
              </dd>
            </div>
          ))}
        </dl>
        <p className="mt-2 text-[11px] text-ink-mid">From the tier change the close recorded on each worker's final, with its reason. A worker is counted once per close. How long Gold lasts is a per-worker reading and is not drawn here.</p>
      </section>
    </div>
  )
}
